
// Utility helper functions
const formatDate = (dateStr) => {
  if (!dateStr || dateStr === "-1" || dateStr === "") return "";
  try {
    const cleanDate = dateStr.split("+")[0];
    const d = new Date(cleanDate);
    if (isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    // Adjusted to DD-MM-YYYY to match the requirement
    return `${day}-${month}-${year}`;
  } catch {
    return dateStr;
  }
};

const getAccountType = (symbol) => {
  const map = {
    "01": "Auto Loan",
    "02": "Housing Loan",
    "03": "Property Loan",
    "04": "Business Loan",
    "05": "Personal Loan",
    "06": "Personal Loan",
    10: "Credit Card",
  };
  return map[symbol] || "Personal Loan";
};

const getOwnership = (symbol) => {
  const map = { 1: "Individual", 2: "Joint", 3: "Guarantor" };
  return map[symbol] || "Individual";
};

// Process monthly payment status into a flat timeline sequence MM-YY
const processPaymentHistoryTimeline = (monthlyList) => {
  if (!monthlyList || !Array.isArray(monthlyList)) return [];

  const timeline = [];
  monthlyList.forEach((item) => {
    if (!item.date) return;
    const cleanDate = item.date.split("+")[0];
    const d = new Date(cleanDate);
    if (isNaN(d.getTime())) return;

    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = String(d.getFullYear()).slice(-2); // Get last 2 digits of year

    timeline.push({
      dateObj: d,
      displayDate: `${month}-${year}`,
      status: item.status || "0",
    });
  });

  // Sort descending (newest first, left to right as per requirement)
  return timeline.sort((a, b) => b.dateObj - a.dateObj);
};

export default function ConsumerAccountDetails({ accounts = [] }) {
  // Demo fallback array to match the visual mockups if no accounts are passed

  const accountList = accounts && accounts.length > 0 ? accounts : [];

  return (
    <div className="w-full max-w-6xl mx-auto px-4 pt-1 pb-2 bg-white font-sans text-sm print:p-0">
      
      {/* SECTION TITLE */}
      <h2 className="text-base text-gray-500 -mb-2 font-medium uppercase tracking-wide">
        ACCOUNT(S):
      </h2>

      {accountList?.length > 0 && accountList.map((account, index) => {
        const tradeline = account?.Tradeline || {};
        const grantedTrade = tradeline?.GrantedTrade || {};
        const payHistory = grantedTrade?.PayStatusHistory?.MonthlyPayStatus || [];

        const timeline = processPaymentHistoryTimeline(payHistory);

        // Alternating background colors
        const bgColor = index % 2 === 0 ? "bg-white" : "bg-gray-200";

        return (
          <div key={index} className={` pb-2 ${bgColor} px-3 pt-1`}>
            
            {/* COLUMN HEADERS */}
            <div className="grid grid-cols-4 gap-4 mb-2 text-[9px] font-bold text-[#00a1de] uppercase tracking-wide">
              <div>ACCOUNT</div>
              <div>DATES</div>
              <div>AMOUNTS</div>
              <div>STATUS</div>
            </div>

            {/* 4-COLUMN DATA GRID */}
            <div className="grid grid-cols-4 gap-4 text-[9px] text-gray-600 mb-2 uppercase leading-[1.4]">
              
              {/* COL 1: ACCOUNT */}
              <div className="flex flex-col ">
                <div className="font-medium">
                  MEMBER NAME:<span className="font-bold text-black">{tradeline.creditorName || ""}</span>
                </div>
                <div className="flex flex-wrap font-medium">
                  <span className="whitespace-nowrap">ACCOUNT</span>
                  <span className="whitespace-nowrap">NUMBER:<span className="font-bold text-black">{tradeline.accountNumber || ""}</span></span>
                </div>
                <div className="font-medium">
                  TYPE:<span className="font-bold text-black capitalize">{getAccountType(account.accountTypeSymbol)}</span>
                </div>
                <div className="font-medium">
                  OWNERSHIP: <span className="font-bold text-black capitalize">{getOwnership(tradeline.AccountDesignator?.symbol)}</span>
                </div>
                <div className="normal-case">
                  <span className="uppercase font-medium">Amount Overdue:</span><span className="font-bold text-black">{tradeline.AmountOverdue || "0"}</span>
                </div>
              </div>

              {/* COL 2: DATES */}
              <div className="flex flex-col ">
                <div className="font-medium">
                  OPENED:<span className="font-bold text-black">{formatDate(tradeline.dateOpened)}</span>
                </div>
                <div className="flex flex-col font-medium">
                  <span>REPORTED AND</span>
                  <span>CERTIFIED:<span className="font-bold text-black">{formatDate(tradeline.dateReported)}</span></span>
                </div>
                <div className="font-medium">
                  PMT HIST START:<span className="font-bold text-black">{formatDate(grantedTrade.PayStatusHistory?.startDate)}</span>
                </div>
                <div className="font-medium">
                  PMT HIST END:<span className="font-bold text-black">{formatDate(grantedTrade.PayStatusHistory?.endDate)}</span>
                </div>
                <div className="font-medium">
                  LAST PAYMENT:<span className="font-bold text-black">{formatDate(grantedTrade.dateLastPayment)}</span>
                </div>
              </div>

              {/* COL 3: AMOUNTS */}
              <div className="flex flex-col ">
                <div className="font-medium">
                  HIGH CREDIT AMOUNT:<span className="font-bold text-black">{tradeline.highBalance || "0"}</span>
                </div>
                <div className="font-medium">
                  CURRENT BALANCE:<span className="font-bold text-black">{tradeline.currentBalance || "0"}</span>
                </div>
                <div className="font-medium">
                  EMI:<span className="font-bold text-black">{grantedTrade.EMIAmount || "-1"}</span>
                </div>
                <div className="font-medium">
                  PAYMENT FREQUENCY:<span className="font-bold text-black">{grantedTrade.PaymentFrequency?.symbol || ""}</span>
                </div>
                <div className="font-medium">
                  REPAYMENT TENURE:<span className="font-bold text-black">{grantedTrade.termMonths || "-1"}</span>
                </div>
              </div>

              {/* COL 4: STATUS */}
              <div className="flex flex-col ">
                <div className="font-medium">
                  ACCOUNT CLOSED:<span className="font-bold text-black">{formatDate(tradeline.dateClosed)}</span>
                </div>
                <div className="font-medium">
                  SETTLEMENT AMOUNT:<span className="font-bold text-black">{tradeline.settlementAmount !== "-1" ? tradeline.settlementAmount : ""}</span>
                </div>
                <div className="font-medium">
                  WRITTEN-OFF
                </div>
                <div className="font-medium">
                  AMOUNT(PRINCIPAL):<span className="font-bold text-black">{tradeline.writtenOffPrincipal !== "-1" ? tradeline.writtenOffPrincipal : ""}</span>
                </div>
                <div className="font-medium">
                  WRITTEN-OFF AMOUNT(Total):<span className="font-bold text-black">{tradeline.writtenOffAmtTotal !== "-1" ? tradeline.writtenOffAmtTotal : ""}</span>
                </div>
              </div>
            </div>

            {/* PAYMENT HISTORY TIMELINE */}
            <div>
              <h3 className="text-[9px] font-bold text-[#00a1de] uppercase tracking-wide mb-1">
                DAYS PAST DUE/ASSET CLASSIFICATION (UP TO 36 MONTHS; LEFT TO RIGHT)
              </h3>
              
              <div className="flex flex-wrap gap-x-6 ">
                {timeline.map((item, idx) => (
                  <div key={idx} className="flex flex-col items-center justify-center text-[9px] font-bold text-black">
                    <span className="-mb-2">{item.status}</span>
                    <span>{item.displayDate}</span>
                  </div>
                ))}
              </div>
            </div>
            
          </div>
        );
      })}
    </div>
  );
}